import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Plus, Pencil, Eye, Trash2, Search } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth/store";
import { formatDate } from "@/lib/format/date";
import { translateApiError } from "@/lib/errors/error-map";
import { toast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { MultiSelect } from "@/components/ui/multi-select";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";

interface Supplier {
  id: string;
  name: string;
}
interface Category {
  id: string;
  name: string;
  children?: Category[];
}
function flattenCats(cats: Category[], prefix = ""): { id: string; name: string }[] {
  const out: { id: string; name: string }[] = [];
  for (const c of cats) {
    out.push({ id: c.id, name: prefix + c.name });
    if (c.children?.length) out.push(...flattenCats(c.children, prefix + "— "));
  }
  return out;
}

interface StockImport {
  id: string;
  code: string;
  supplierName: string | null;
  status: string;
  totalCost: string;
  importedByEmail: string | null;
  createdAt: string;
  committedAt: string | null;
}

interface ImportsResponse {
  data: StockImport[];
  meta: { total: number };
}

const STATUS_LABELS: Record<string, string> = {
  draft: "Nháp",
  committed: "Đã nhập",
  cancelled: "Đã huỷ",
};

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-yellow-100 text-yellow-700",
  committed: "bg-green-100 text-green-700",
  cancelled: "bg-muted text-muted-foreground",
};

function fmtCost(cost: string) {
  const n = parseFloat(cost);
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(n);
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return formatDate(iso);
}

export function ImportListPage() {
  const qc = useQueryClient();
  // Viewer (chị Thủy) chỉ XEM phiếu nhập — ẩn nút Tạo/Xóa thay vì để bấm rồi
  // nhận 403. Các role khác vào được trang này đều có quyền ghi.
  const canMutate = useAuthStore((s) => s.user)?.role !== "viewer";

  // Filters: q (code/NCC/SKU/tên SP/IMEI), supplier, category, date range.
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers"],
    queryFn: () => apiClient.get<{ data: Supplier[] }>("/v1/suppliers").then((r) => r.data.data),
  });
  const { data: catTree = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: () => apiClient.get<{ data: Category[] }>("/v1/categories").then((r) => r.data.data),
  });
  const flatCats = flattenCats(catTree);

  const params = {
    limit: 50,
    offset: 0,
    ...(debouncedQ ? { q: debouncedQ } : {}),
    ...(supplierId ? { supplier_id: supplierId } : {}),
    ...(categoryIds.length ? { category_ids: categoryIds.join(",") } : {}),
    ...(dateFrom ? { date_from: dateFrom } : {}),
    ...(dateTo ? { date_to: dateTo } : {}),
  };

  const { data: result, isLoading } = useQuery({
    queryKey: ["imports", params],
    queryFn: () =>
      apiClient.get<ImportsResponse>("/v1/imports", { params }).then((r) => r.data),
    placeholderData: (prev) => prev,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/v1/imports/${id}`),
    onSuccess: () => {
      toast.success("Đã xóa phiếu");
      qc.invalidateQueries({ queryKey: ["imports"] });
    },
    onError: (err) => toast.error(translateApiError(err)),
  });

  function handleDelete(si: StockImport) {
    const msg =
      si.status === "committed"
        ? `Xóa phiếu ${si.code}? Tồn kho + IMEI của phiếu này sẽ bị đảo ngược (chặn nếu có IMEI đã bán).`
        : `Xóa phiếu ${si.code}?`;
    if (window.confirm(msg)) deleteMutation.mutate(si.id);
  }

  const imports: StockImport[] = result?.data ?? [];
  const total = result?.meta?.total ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Phiếu nhập hàng</h1>
        {canMutate && (
          <Link to="/inventory/imports/new">
            <Button size="sm" className="gap-1.5">
              <Plus size={16} />
              Tạo phiếu
            </Button>
          </Link>
        )}
      </div>

      {/* Search & filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-8"
            placeholder="Tìm mã phiếu, NCC, SKU, tên SP, IMEI…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <SearchableSelect
          value={supplierId}
          onChange={setSupplierId}
          options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
          placeholder="— Nhà cung cấp —"
          className="w-[200px]"
        />
        <MultiSelect
          values={categoryIds}
          onChange={setCategoryIds}
          options={flatCats.map((c) => ({ value: c.id, label: c.name }))}
          placeholder="— Nhóm hàng —"
          className="w-[200px]"
        />
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" />
        {(q || supplierId || categoryIds.length || dateFrom || dateTo) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setQ(""); setSupplierId(""); setCategoryIds([]); setDateFrom(""); setDateTo(""); }}
          >
            Xóa lọc
          </Button>
        )}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Đang tải…</p>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{total} phiếu</p>
          <Table>
            <THead>
              <Tr>
                <Th>Mã phiếu</Th>
                <Th>Nhà cung cấp</Th>
                <Th>Trạng thái</Th>
                <Th className="text-right">Tổng tiền</Th>
                <Th>Người tạo</Th>
                <Th>Ngày tạo</Th>
                <Th>Ngày nhập</Th>
                <Th className="text-right">Thao tác</Th>
              </Tr>
            </THead>
            <TBody>
              {imports.length === 0 && (
                <Tr>
                  <Td colSpan={8} className="text-center text-muted-foreground">
                    Chưa có phiếu nhập
                  </Td>
                </Tr>
              )}
              {imports.map((si) => (
                <Tr key={si.id}>
                  <Td>
                    <Link
                      to="/inventory/imports/$id"
                      params={{ id: si.id }}
                      className="font-mono text-sm font-medium hover:underline"
                    >
                      {si.code}
                    </Link>
                  </Td>
                  <Td>
                    {si.supplierName ? (
                      <Link
                        to="/admin/suppliers"
                        className="text-primary hover:underline"
                        title="Xem thông tin nhà cung cấp"
                      >
                        {si.supplierName}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </Td>
                  <Td>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[si.status] ?? ""}`}>
                      {STATUS_LABELS[si.status] ?? si.status}
                    </span>
                  </Td>
                  <Td className="text-right">{fmtCost(si.totalCost)}</Td>
                  <Td className="text-xs text-muted-foreground">{si.importedByEmail ?? "—"}</Td>
                  <Td>{fmtDate(si.createdAt)}</Td>
                  <Td>{fmtDate(si.committedAt)}</Td>
                  <Td>
                    <div className="flex items-center justify-end gap-1">
                      <Link to="/inventory/imports/$id" params={{ id: si.id }}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title={canMutate ? "Sửa / xem" : "Xem phiếu"}
                        >
                          {canMutate ? <Pencil size={14} /> : <Eye size={14} />}
                        </Button>
                      </Link>
                      {canMutate && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive"
                          title="Xóa phiếu"
                          disabled={deleteMutation.isPending}
                          onClick={() => handleDelete(si)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </>
      )}
    </div>
  );
}
